/**
 * Pure helpers for the "Sign in with Tabbio" consent screen, and the CV
 * selection decisions Settings > Connected apps shares with it.
 *
 * Everything here is platform-free on purpose: the native screen and the web
 * screen render the same decisions, so the decisions themselves live in one
 * place and are unit tested without a renderer
 * (`src/__tests__/developer-platform-consent.test.ts`).
 */
import {
  CONSENT_PRESETS,
  CONSENT_PRESET_SCOPES,
  REQUIRED_USER_SCOPES,
  USER_SCOPES,
  type ConnectedAppCvSummary,
  type ConsentPreset,
  type OAuthAuthorizeRequestParams,
  type UserScope,
} from '@tabbio/types';

/** The main CV sentinel the API accepts in place of a CV id. */
export const CONSENT_MAIN_CV_ID = 'master';

/** Reject absurd inputs early; a real authorize URL is well under this. */
const MAX_ENCODED_TARGET_LENGTH = 3000;

type QueryValue = string | string[] | undefined;

function first(value: QueryValue): string {
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
}

/**
 * Read the standard OAuth authorize parameters out of whatever
 * `useLocalSearchParams` handed us. Snake_case is the wire format (RFC 6749);
 * camelCase is accepted too so a target rebuilt from our own query survives a
 * round trip.
 */
export function parseOAuthAuthorizeParams(
  params: Record<string, QueryValue>,
): OAuthAuthorizeRequestParams {
  const pick = (snake: string, camel: string) => {
    const snakeValue = first(params[snake]);
    return snakeValue || first(params[camel]);
  };

  const state = pick('state', 'state');

  return {
    responseType: pick('response_type', 'responseType') || 'code',
    clientId: pick('client_id', 'clientId'),
    redirectUri: pick('redirect_uri', 'redirectUri'),
    scope: pick('scope', 'scope'),
    state: state ? state : null,
    codeChallenge: pick('code_challenge', 'codeChallenge'),
    codeChallengeMethod:
      pick('code_challenge_method', 'codeChallengeMethod') || 'S256',
  };
}

/**
 * The four things without which there is nothing to show. Everything else the
 * server validates: this only decides whether we can even ask it.
 */
export function isCompleteAuthorizeRequest(
  params: OAuthAuthorizeRequestParams,
): boolean {
  return Boolean(
    params.clientId &&
      params.redirectUri &&
      params.scope &&
      params.codeChallenge,
  );
}

/**
 * Rebuild the authorize query in the wire (snake_case) form. Values are
 * percent-encoded, so the result is pure ASCII: that is what lets the base64url
 * encoding below stay a byte-for-byte transform.
 */
export function buildAuthorizeQuery(
  params: OAuthAuthorizeRequestParams,
): string {
  const entries: [string, string][] = [
    ['response_type', params.responseType || 'code'],
    ['client_id', params.clientId],
    ['redirect_uri', params.redirectUri],
    ['scope', params.scope],
    ['code_challenge', params.codeChallenge],
    ['code_challenge_method', params.codeChallengeMethod || 'S256'],
  ];

  if (params.state) {
    entries.push(['state', params.state]);
  }

  return entries
    .filter(([, value]) => value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

/** Inverse of `buildAuthorizeQuery`, tolerant of an extra leading `?`. */
export function parseAuthorizeQuery(
  query: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  const trimmed = query.startsWith('?') ? query.slice(1) : query;

  for (const pair of trimmed.split('&')) {
    if (!pair) continue;
    const index = pair.indexOf('=');
    const rawKey = index === -1 ? pair : pair.slice(0, index);
    const rawValue = index === -1 ? '' : pair.slice(index + 1);
    if (!rawKey) continue;
    try {
      out[decodeURIComponent(rawKey)] = decodeURIComponent(
        rawValue.replace(/\+/g, ' '),
      );
    } catch {
      // A malformed escape means the whole target is untrustworthy; drop the
      // pair rather than throwing inside a render path.
      continue;
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Post-auth target encoding
// ---------------------------------------------------------------------------

const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * base64url, hand rolled.
 *
 * The target rides as `?next=<target>` on `/auth-complete` and on the login
 * route, both of which interpolate it into a URL as a raw string. Percent
 * encoding the query would put `%`, `&` and `=` back into that position and the
 * target would be cut in half by the first `&`. base64url has none of those
 * characters, so the value survives every hop unchanged. `btoa` is not reliably
 * present on Hermes, hence the manual table.
 */
export function toBase64Url(input: string): string {
  let out = '';

  for (let i = 0; i < input.length; i += 3) {
    const c1 = input.charCodeAt(i) & 0xff;
    const hasC2 = i + 1 < input.length;
    const hasC3 = i + 2 < input.length;
    const c2 = hasC2 ? input.charCodeAt(i + 1) & 0xff : 0;
    const c3 = hasC3 ? input.charCodeAt(i + 2) & 0xff : 0;

    out += BASE64URL_ALPHABET[c1 >> 2];
    out += BASE64URL_ALPHABET[((c1 & 0x03) << 4) | (c2 >> 4)];
    if (!hasC2) break;
    out += BASE64URL_ALPHABET[((c2 & 0x0f) << 2) | (c3 >> 6)];
    if (!hasC3) break;
    out += BASE64URL_ALPHABET[c3 & 0x3f];
  }

  return out;
}

export function fromBase64Url(input: string): string | null {
  let out = '';
  let buffer = 0;
  let bits = 0;

  for (const char of input) {
    const index = BASE64URL_ALPHABET.indexOf(char);
    if (index < 0) return null;
    buffer = (buffer << 6) | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >> bits) & 0xff);
    }
  }

  return out;
}

/** Charset guard used by `normalizePostAuthTarget` in `lib/auth-handoff.ts`. */
export const OAUTH_POST_AUTH_TARGET_PATTERN = /^[A-Za-z0-9_-]+$/;

export function isEncodedOAuthTarget(encoded: string): boolean {
  return (
    encoded.length > 0 &&
    encoded.length <= MAX_ENCODED_TARGET_LENGTH &&
    OAUTH_POST_AUTH_TARGET_PATTERN.test(encoded)
  );
}

/**
 * Park a whole authorize request as a post-auth target. Signing in mid-flow
 * remounts the navigator and drops the URL, so the parameters have to be stored
 * rather than carried (`lib/auth-handoff.ts` explains the race).
 */
export function encodeOAuthPostAuthTarget(query: string): `oauth:${string}` {
  return `oauth:${toBase64Url(query)}`;
}

/** The query string behind an `oauth:` target, or null when it is unusable. */
export function decodeOAuthPostAuthTargetQuery(target: string): string | null {
  if (!target.startsWith('oauth:')) return null;
  const encoded = target.slice('oauth:'.length);
  if (!isEncodedOAuthTarget(encoded)) return null;
  const decoded = fromBase64Url(encoded);
  return decoded && decoded.length > 0 ? decoded : null;
}

// ---------------------------------------------------------------------------
// Scopes and presets
// ---------------------------------------------------------------------------

const USER_SCOPE_SET = new Set<string>(USER_SCOPES);

/** Space separated `scope` parameter to the known user scopes, in catalog order. */
export function parseScopeList(scope: string): UserScope[] {
  const requested = new Set(
    scope
      .split(/[\s+]+/)
      .map((value) => value.trim())
      .filter((value) => USER_SCOPE_SET.has(value)),
  );

  return USER_SCOPES.filter((value) => requested.has(value));
}

/** Keep a granted set in catalog order so comparisons stay stable. */
export function sortScopes(scopes: readonly UserScope[]): UserScope[] {
  const set = new Set(scopes);
  return USER_SCOPES.filter((scope) => set.has(scope));
}

export function isRequiredScope(scope: UserScope): boolean {
  return REQUIRED_USER_SCOPES.includes(scope);
}

/** The scopes a preset grants, clipped to what the app actually asked for. */
export function applyPreset(
  preset: ConsentPreset,
  requested: readonly UserScope[],
): UserScope[] {
  const requestedSet = new Set(requested);
  return CONSENT_PRESET_SCOPES[preset].filter((scope) =>
    requestedSet.has(scope),
  );
}

/**
 * A preset is offered only when the app requested enough for it to differ from
 * a smaller one. Offering "Share my CV" to an app that never asked for the CV
 * is a control that silently does nothing.
 */
export function isPresetAvailable(
  preset: ConsentPreset,
  requested: readonly UserScope[],
): boolean {
  const requestedSet = new Set(requested);
  if (preset === 'cv') return requestedSet.has('cv:read');
  if (preset === 'full') {
    return requestedSet.has('cv:read') && requestedSet.has('cv:contact');
  }
  return true;
}

export function availablePresets(
  requested: readonly UserScope[],
): ConsentPreset[] {
  return CONSENT_PRESETS.filter((preset) => isPresetAvailable(preset, requested));
}

function sameScopes(left: readonly UserScope[], right: readonly UserScope[]) {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((scope) => rightSet.has(scope));
}

/**
 * Which preset the current selection corresponds to, or null when the user has
 * built a combination no preset describes (a partial grant is legal OAuth).
 */
export function derivePreset(
  requested: readonly UserScope[],
  granted: readonly UserScope[],
): ConsentPreset | null {
  for (const preset of CONSENT_PRESETS) {
    if (!isPresetAvailable(preset, requested)) continue;
    if (sameScopes(applyPreset(preset, requested), granted)) return preset;
  }
  return null;
}

/**
 * Flip one scope. Required identity scopes cannot be dropped and scopes the app
 * never requested cannot be added, so a bad toggle is a no-op rather than a
 * request the server will reject.
 */
export function toggleScope(
  granted: readonly UserScope[],
  scope: UserScope,
  requested: readonly UserScope[],
): UserScope[] {
  if (!requested.includes(scope)) return sortScopes(granted);

  const has = granted.includes(scope);

  if (has) {
    if (isRequiredScope(scope)) return sortScopes(granted);
    const next = granted.filter((value) => value !== scope);
    // Private contact details are meaningless without the CV they sit in.
    return sortScopes(
      scope === 'cv:read' ? next.filter((value) => value !== 'cv:contact') : next,
    );
  }

  const next = [...granted, scope];
  // Turning the private details on implies sharing the CV they belong to.
  if (scope === 'cv:contact' && requested.includes('cv:read')) {
    next.push('cv:read');
  }
  return sortScopes(next);
}

/** The grant the screen starts from: an existing connection, else a preset. */
export function initialGrantedScopes(
  requested: readonly UserScope[],
  existing: readonly UserScope[] | null,
): UserScope[] {
  if (existing && existing.length > 0) {
    const requestedSet = new Set(requested);
    const kept = sortScopes(existing.filter((scope) => requestedSet.has(scope)));
    // Identity is never optional when requested, even if an older grant is
    // missing it.
    const withRequired = sortScopes([
      ...kept,
      ...REQUIRED_USER_SCOPES.filter((scope) => requestedSet.has(scope)),
    ]);
    if (withRequired.length > 0) return withRequired;
  }

  const preferred: ConsentPreset = isPresetAvailable('full', requested)
    ? 'full'
    : isPresetAvailable('cv', requested)
      ? 'cv'
      : 'signin';

  return applyPreset(preferred, requested);
}

/** `master` is the API's sentinel for "follow my main CV"; the wire wants null. */
export function normalizeConsentCvId(cvId: string | null): string | null {
  return !cvId || cvId === CONSENT_MAIN_CV_ID ? null : cvId;
}

/**
 * The inverse: the picker selects by row id, and the main CV row is `master`.
 * A deleted selection keeps its stale id, so the picker ticks nothing rather
 * than implying the app reads the main CV.
 */
export function consentCvSelectionId(cvId: string | null): string {
  return cvId ?? CONSENT_MAIN_CV_ID;
}

/**
 * Whether the CV a connected app was given is gone (audit F09): the user
 * pinned one specific CV for the app and then deleted it, so the app is
 * refused until they choose again. Settings > Connected apps reads this on
 * both platforms to show the warning row. An API that predates `status`
 * sends none, which means available.
 */
export function isConnectedAppCvUnavailable(
  cv: ConnectedAppCvSummary | null | undefined,
): boolean {
  return cv?.status === 'unavailable';
}

/**
 * Scope ids carry a colon (`cv:read`), which i18next reads as a namespace
 * separator, so copy is keyed by a flattened segment instead.
 */
export function scopeCopyKey(scope: string): string {
  return scope.replace(/:(.)/g, (_match, char: string) => char.toUpperCase());
}
